import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';

const levelStyle: Record<string, string> = {
  已确认改编: 'bg-primary-500 text-background-50',
  高度疑似: 'bg-accent-500 text-primary-950',
  部分取材: 'bg-secondary-500 text-background-50',
  声明存疑: 'bg-foreground-700 text-background-50',
};

export default function DetailHero({ drama }: { drama: Drama }) {
  const facts = [
    { label: '上线年份', value: `${drama.year} 年`, icon: 'ri-calendar-line' },
    { label: '播出平台', value: drama.platform, icon: 'ri-tv-2-line' },
    { label: '剧集规格', value: `${drama.episodes} 集 · ${drama.duration}`, icon: 'ri-film-line' },
    { label: '题材分区', value: drama.category, icon: 'ri-price-tag-3-line' },
  ];

  return (
    <section className="w-full bg-background-100 border-b border-background-200 archive-grid">
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10">
          <div className="w-40 sm:w-52 md:w-64 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-300 bg-background-200">
              <img
                src={drama.cover}
                alt={`《${drama.title}》短剧海报，题材：${drama.category}`}
                title={`《${drama.title}》- 真实事件改编与原型人物对照`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${levelStyle[drama.confirmLevel] || 'bg-foreground-700 text-background-50'}`}>
                <i className="ri-shield-check-line mr-1"></i>
                {drama.confirmLevel}
              </span>
              <span className="px-3 py-1 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 whitespace-nowrap">
                {drama.category}
              </span>
              <span className="px-3 py-1 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 whitespace-nowrap">
                热度 {drama.hot}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
              《{drama.title}》
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              {drama.subtitle}
            </p>

            <div className="mt-6 flex items-end gap-6">
              <div>
                <p className="text-xs text-foreground-500 mb-1">综合还原度</p>
                <p className="font-heading text-3xl md:text-4xl font-black text-primary-600 leading-none">
                  {drama.adaptationScore}
                  <span className="text-base font-medium text-foreground-500 ml-1">%</span>
                </p>
              </div>
              <div className="flex-1 pb-1.5">
                <div className="h-2 w-full rounded-full bg-background-300 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${drama.adaptationScore}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-foreground-500">
                  五维评分综合所得，分数越高表示越贴近真实事件
                </p>
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {facts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-md bg-background-50 border border-background-200"
                >
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-700">
                    <i className={`${fact.icon} text-base`}></i>
                  </span>
                  <span className="min-w-0">
                    <dt className="text-[11px] text-foreground-500">{fact.label}</dt>
                    <dd className="text-sm text-foreground-900 truncate">{fact.value}</dd>
                  </span>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap gap-2">
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-md bg-accent-50 border border-accent-200 text-xs text-accent-900 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {drama.editor}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读约 {drama.readMinutes} 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                核对日期 {drama.publishDate}
              </span>
              <Link to="/dramas" className="text-primary-600 hover:text-primary-700 transition-colors">
                返回档案库
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}